// client/src/coms/presence/presenceClient.js
// One shared presence feed for every surface (member list, voice participants, your own avatar, settings):
// a single heartbeat and a single /api/presence/online poll, however many components use it.
// Your own status lives on the server (routes/presence.js), so every device shows the same one; a change made here
// is applied at once locally and announced with PRESENCE_EVENT, other members see it on their next poll.
import { useEffect, useSyncExternalStore } from 'react';

export const PRESENCE_EVENT = 'vox-presence-changed';
const POLL_MS = 8000;
const PING_MS = 30000;

const EMPTY = { loaded: false, byId: new Map(), me: null, skewMs: 0 };
let snapshot = EMPTY;
const listeners = new Set();
let users = 0;
let pollTimer = null;
let pingTimer = null;
let inflight = null;

function emit(next) {
  snapshot = next;
  listeners.forEach((fn) => {
    try {
      fn();
    } catch {}
  });
}

async function api(method, path, body) {
  const res = await fetch(`/api/presence${path}`, {
    method,
    credentials: 'include',
    cache: 'no-store',
    headers: body ? { Accept: 'application/json', 'Content-Type': 'application/json' } : { Accept: 'application/json' },
    body: body ? JSON.stringify(body) : undefined,
  });
  const data = await res.json().catch(() => null);
  if (!res.ok || !data || data.ok === false) {
    const err = new Error((data && data.error) || `Could not reach the server (${res.status}).`);
    err.status = res.status;
    throw err;
  }
  return data;
}

function normaliseMe(m) {
  if (!m) return null;
  return { status: String(m.status || 'online'), until_ms: Number(m.until_ms) || 0, indefinite: !!m.indefinite, custom_text: String(m.custom_text || '') };
}

export async function refreshPresence() {
  if (inflight) return inflight;
  inflight = (async () => {
    try {
      const data = await api('GET', '/online?within_ms=90000');
      const byId = new Map();
      for (const r of Array.isArray(data.online) ? data.online : []) {
        const id = Number(r && r.user_id);
        if (id > 0) byId.set(id, { status: String(r.status || 'online'), custom_text: String(r.custom_text || '') });
      }
      const skewMs = data.server_now_ms ? Number(data.server_now_ms) - Date.now() : 0;
      emit({ loaded: true, byId, me: normaliseMe(data.me), skewMs: Number.isFinite(skewMs) ? skewMs : 0 });
    } catch {
      if (!snapshot.loaded) emit({ ...EMPTY, loaded: true });
    } finally {
      inflight = null;
    }
  })();
  return inflight;
}

function ping() {
  api('POST', '/ping').catch(() => {});
}

function hidden() {
  try {
    return typeof document !== 'undefined' && document.visibilityState === 'hidden';
  } catch {
    return false;
  }
}

function onVisible() {
  if (!hidden()) {
    ping();
    refreshPresence();
  }
}

function onLocalChange(e) {
  const s = e && e.detail;
  if (s && s.status) emit({ ...snapshot, me: normaliseMe(s) });
  refreshPresence();
}

function start() {
  ping();
  refreshPresence();
  pingTimer = setInterval(ping, PING_MS);
  pollTimer = setInterval(() => {
    if (!hidden()) refreshPresence();
  }, POLL_MS);
  try {
    document.addEventListener('visibilitychange', onVisible);
    window.addEventListener('focus', onVisible);
    window.addEventListener(PRESENCE_EVENT, onLocalChange);
  } catch {}
}

function stop() {
  clearInterval(pingTimer);
  clearInterval(pollTimer);
  pingTimer = null;
  pollTimer = null;
  try {
    document.removeEventListener('visibilitychange', onVisible);
    window.removeEventListener('focus', onVisible);
    window.removeEventListener(PRESENCE_EVENT, onLocalChange);
  } catch {}
}

function subscribe(fn) {
  listeners.add(fn);
  return () => listeners.delete(fn);
}

function getSnapshot() {
  return snapshot;
}

// { loaded, byId: Map<userId, { status, custom_text }>, me, skewMs }. Users missing from byId are offline (or
// invisible, which looks the same on purpose).
export function usePresence() {
  useEffect(() => {
    users += 1;
    if (users === 1) start();
    return () => {
      users -= 1;
      if (users === 0) stop();
    };
  }, []);
  return useSyncExternalStore(subscribe, getSnapshot, getSnapshot);
}

// The status another member should be shown with: 'online' | 'idle' | 'dnd' | 'offline' (+ 'invisible' for you).
export function statusFor(presence, userId, myUserId) {
  const id = Number(userId) || 0;
  if (!id) return { status: 'offline', custom_text: '' };
  if (myUserId && id === Number(myUserId) && presence.me) {
    // Your own row: what you chose (Invisible included), if this client is connected.
    return { status: presence.me.status, custom_text: presence.me.custom_text };
  }
  return presence.byId.get(id) || { status: 'offline', custom_text: '' };
}

function announce(s) {
  try {
    window.dispatchEvent(new CustomEvent(PRESENCE_EVENT, { detail: s }));
    // Older listeners of the Appear offline switch.
    const v = s.status === 'invisible' ? 1 : 0;
    window.dispatchEvent(new CustomEvent('presence-show-offline-changed', { detail: { value: v } }));
    window.dispatchEvent(new CustomEvent('coms-appear-offline-changed', { detail: { value: !!v } }));
  } catch {}
  return s;
}

export async function getMyStatus() {
  return normaliseMe(await api('GET', '/status'));
}

// duration: null (until you turn it off) or { value, unit }.
export async function setMyStatus(status, duration = null, customText) {
  const body = { status, duration };
  if (customText !== undefined) body.custom_text = customText;
  return announce(normaliseMe(await api('PUT', '/status', body)));
}

export async function setMyStatusText(customText) {
  return announce(normaliseMe(await api('PUT', '/status/text', { custom_text: customText })));
}
